import { useRef, useEffect, useCallback, useState } from "react";
import MultiEmotionWorker from "@/workers/multiEmotion.worker.js";

export default function useMultiEmotionWorker() {
  const workerRef = useRef(null);
  const pendingRef = useRef(new Map());
  const [workerAvailable, setWorkerAvailable] = useState(false);

  useEffect(() => {
    const worker = new MultiEmotionWorker();
    workerRef.current = worker;

    setWorkerAvailable(true);

    const pendingMapAtMount = pendingRef.current;

    const onMessage = (e) => {
      const { id, result, error } = e.data || {};
      const entry = pendingMapAtMount.get(id);
      if (!entry) {
        return;
      }
      const { resolve, reject, timeoutId } = entry;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      error ? reject(new Error(error)) : resolve(result);
      pendingMapAtMount.delete(id);
    };

    const onError = (err) => {
      for (const { reject, timeoutId } of pendingMapAtMount.values()) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        try {
          reject(err);
        } catch (err) {}
      }
      pendingMapAtMount.clear();
    };

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    return () => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);

      try {
        worker.terminate();
      } catch (err) {}
      for (const { reject, timeoutId } of pendingMapAtMount.values()) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        try {
          reject(new Error("Worker terminated."));
        } catch (err) {}
      }
      pendingMapAtMount.clear();
      workerRef.current = null;
      setWorkerAvailable(false);
    };
  }, []);

  const computeEmotion = useCallback(
    (emotion, stateForWorker, timeoutMs = 30000) => {
      const worker = workerRef.current;
      if (!worker) {
        return Promise.reject(new Error("No worker available"));
      }
      const id = crypto.randomUUID();

      return new Promise((resolve, reject) => {
        let timeoutId = null;

        if (timeoutMs) {
          timeoutId = setTimeout(() => {
            const pending = pendingRef.current.get(id);
            if (pending) {
              pendingRef.current.delete(id);
              try {
                pending.reject(new Error("Worker timeout"));
              } catch (e) {}
            }
          }, timeoutMs);
        }

        pendingRef.current.set(id, { resolve, reject, timeoutId });

        try {
          worker.postMessage({ id, payload: { emotion, stateForWorker } });
        } catch (err) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          pendingRef.current.delete(id);
          reject(err);
        }
      });
    },
    []
  );

  return { computeEmotion, workerAvailable };
}
